import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  Input,
  Select,
  Tag,
  Modal,
  message,
  Row,
  Col,
  Tooltip,
  Dropdown,
  MenuProps,
  Typography,
  Statistic,
  Avatar,
  Divider,
  Badge,
  Empty,
  Spin
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  ImportOutlined,
  MoreOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  LockOutlined,
  UnlockOutlined,
  MonitorOutlined
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';

import { UserService, userUtils } from '../../../services/userService';
import type { User, UserListParams, UserStatistics } from '../../../types/user';
import './index.css';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface UserListState {
  users: User[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  filters: UserListParams;
  selectedRowKeys: React.Key[];
  statistics: UserStatistics;
}

const UserList: React.FC = () => {
  const navigate = useNavigate();
  
  const [state, setState] = useState<UserListState>({
    users: [],
    loading: false,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
    filters: {},
    selectedRowKeys: [],
    statistics: {
      total: 0,
      active: 0,
      inactive: 0,
      totalDepartments: 0,
      totalPositions: 0,
      totalPermissionGroups: 0,
    },
  });

  // Load initial data
  useEffect(() => {
    loadStatistics();
    loadUsers();
  }, []);

  // Load statistics
  const loadStatistics = async () => {
    try {
      const statistics = await UserService.getUserStatistics();
      setState(prev => ({ ...prev, statistics }));
    } catch (error) {
      console.error('載入統計資料失敗:', error);
    }
  };

  // Load users data
  const loadUsers = useCallback(async (params?: UserListParams) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const requestParams = {
        page: state.pagination.current,
        limit: state.pagination.pageSize,
        ...state.filters,
        ...params,
      };

      const response = await UserService.listUsers(requestParams);
      
      setState(prev => ({
        ...prev,
        users: response.items,
        pagination: {
          current: response.pagination.page,
          pageSize: response.pagination.limit,
          total: response.pagination.total,
        },
        loading: false,
      }));
    } catch (error) {
      message.error(`載入使用者清單失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [state.pagination.current, state.pagination.pageSize, state.filters]);

  // Handle search
  const handleSearch = (value: string) => {
    const newFilters = {
      ...state.filters,
      search: value,
    };
    setState(prev => ({
      ...prev,
      filters: newFilters,
      pagination: { ...prev.pagination, current: 1 },
    }));
    loadUsers(newFilters);
  };

  // Handle filter change
  const handleFilterChange = (key: string, value: any) => {
    const newFilters = {
      ...state.filters,
      [key]: value,
    };
    setState(prev => ({
      ...prev,
      filters: newFilters,
      pagination: { ...prev.pagination, current: 1 },
    }));
    loadUsers(newFilters);
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    const newPagination = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 20,
      total: state.pagination.total,
    };

    setState(prev => ({ ...prev, pagination: newPagination }));
    
    loadUsers({
      page: newPagination.current,
      limit: newPagination.pageSize,
    });
  };

  // Handle row selection
  const handleSelectChange = (selectedRowKeys: React.Key[]) => {
    setState(prev => ({ ...prev, selectedRowKeys }));
  };

  // Handle create user
  const handleCreate = () => {
    navigate('/user/create');
  };

  // Handle view user
  const handleView = (userId: string) => {
    navigate(`/user/view/${userId}`);
  };

  // Handle edit user
  const handleEdit = (userId: string) => {
    navigate(`/user/edit/${userId}`);
  };

  // Handle toggle user status
  const handleToggleStatus = async (user: User) => {
    const action = user.isActive ? '停用' : '啟用';
    Modal.confirm({
      title: `確認${action}使用者`,
      content: `確定要${action}使用者 "${user.userName}" 嗎？`,
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await UserService.updateUserStatus(user.userId, {
            isActive: !user.isActive,
            reason: `手動${action}`,
          });
          message.success(`使用者${action}成功`);
          loadUsers();
          loadStatistics();
        } catch (error) {
          message.error(`${action}失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Handle delete user
  const handleDelete = (user: User) => {
    console.log('[UserList] Delete button clicked for user:', user.userId, user.userName);
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除使用者 "${user.userName}" 嗎？此操作無法復原。`,
      okText: '確定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        console.log('[UserList] User confirmed deletion for:', user.userId);
        try {
          await UserService.deleteUser(user.userId);
          message.success('使用者刪除成功');
          console.log('[UserList] Delete successful, reloading users...');
          loadUsers();
          loadStatistics();
        } catch (error) {
          console.error('[UserList] Delete failed:', error);
          message.error(`刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
      onCancel: () => {
        console.log('[UserList] User cancelled deletion');
      }
    });
  };

  // Handle reset password
  const handleResetPassword = (user: User) => {
    Modal.confirm({
      title: '重置密碼',
      content: `確定要重置使用者 "${user.userName}" 的密碼嗎？新密碼將發送到該使用者的郵箱。`,
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const newPassword = 'Temp123456'; // Generate temporary password
          await UserService.resetUserPassword(user.userId, { newPassword });
          message.success('密碼重置成功，新密碼已發送到使用者郵箱');
        } catch (error) {
          message.error(`重置密碼失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Handle unlock user
  const handleUnlockUser = (user: User) => {
    Modal.confirm({
      title: '解鎖使用者',
      content: (
        <div>
          <p>確定要解鎖使用者 "{user.userName}" 嗎？</p>
          <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
            該使用者因連續 {user.failedLoginCount || 0} 次登入失敗而被鎖定
          </p>
          {user.lockReason && (
            <p style={{ fontSize: '12px' }}>
              鎖定原因：{user.lockReason}
            </p>
          )}
        </div>
      ),
      okText: '確定解鎖',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          await UserService.unlockUser(user.userId);
          message.success('使用者已成功解鎖');
          loadUsers(); // 重新載入使用者列表
        } catch (error) {
          message.error(`解鎖失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
        }
      },
    });
  };

  // Batch actions
  const handleBatchAction = (action: string) => {
    if (state.selectedRowKeys.length === 0) {
      message.warning('請選擇要操作的使用者');
      return;
    }

    const selectedUsers = state.users.filter(u => state.selectedRowKeys.includes(u.userId));
    
    if (action === 'delete') {
      Modal.confirm({
        title: '批量刪除確認',
        content: `確定要刪除選中的 ${selectedUsers.length} 個使用者嗎？此操作無法復原。`,
        okText: '確定',
        cancelText: '取消',
        okType: 'danger',
        onOk: async () => {
          try {
            for (const user of selectedUsers) {
              await UserService.deleteUser(user.userId);
            }
            message.success(`成功刪除 ${selectedUsers.length} 個使用者`);
            setState(prev => ({ ...prev, selectedRowKeys: [] }));
            loadUsers();
            loadStatistics();
          } catch (error) {
            message.error(`批量刪除失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
          }
        },
      });
    }
  };

  // Action menu for each row
  const getActionMenu = (user: User): MenuProps => ({
    items: [
      {
        key: 'view',
        label: '檢視詳情',
        icon: <EyeOutlined />,
        onClick: () => handleView(user.userId),
      },
      {
        key: 'edit',
        label: '編輯',
        icon: <EditOutlined />,
        onClick: () => handleEdit(user.userId),
      },
      {
        key: 'permissions',
        label: '權限設定',
        icon: <SettingOutlined />,
        onClick: () => navigate(`/user/permissions/${user.userId}`),
      },
      {
        type: 'divider',
      },
      {
        key: 'toggle-status',
        label: user.isActive ? '停用' : '啟用',
        icon: user.isActive ? <LockOutlined /> : <UnlockOutlined />,
        onClick: () => handleToggleStatus(user),
      },
      {
        key: 'reset-password',
        label: '重置密碼',
        icon: <LockOutlined />,
        onClick: () => handleResetPassword(user),
      },
      // 如果使用者被鎖定，顯示解鎖選項
      ...(user.isLocked ? [{
        key: 'unlock',
        label: '解鎖帳號',
        icon: <UnlockOutlined />,
        danger: true,
        onClick: () => handleUnlockUser(user),
      }] : []),
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '刪除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(user),
      },
    ],
  });

  // Table columns definition
  const columns: ColumnsType<User> = [
    {
      title: '使用者資訊',
      key: 'user_info',
      width: 280,
      render: (_, user: User) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />} 
            style={{ 
              backgroundColor: user.isActive ? '#1890ff' : '#d9d9d9',
              color: user.isActive ? '#fff' : '#8c8c8c'
            }}
          >
            {user.userName?.charAt(0) || 'U'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#262626' }}>
              {user.userName}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {UserService.formatUserId(user.userId)}
            </div>
            {user.email && (
              <div style={{ fontSize: '12px', color: '#595959' }}>
                ✉️ {user.email}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '職位',
      dataIndex: 'positions',
      key: 'positions',
      width: 180,
      render: (positions: User['positions']) => {
        if (!positions || positions.length === 0) {
          return <Text type="secondary">未分配</Text>;
        }
        
        const mainPosition = positions.find(p => p.isMain);
        const otherPositions = positions.filter(p => !p.isMain);
        
        return (
          <div>
            {mainPosition && (
              <Tag color="blue">{mainPosition.positionName}</Tag>
            )}
            {otherPositions.map((pos, index) => (
              <Tag key={index} color="default">{pos.positionName}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, user: User) => {
        // 檢查是否被鎖定 - 優先使用後端計算的 isLocked，其次檢查 lockTime
        const isLocked = user.isLocked || (user.lockTime && new Date(user.lockTime) > new Date());
        
        if (isLocked) {
          return (
            <Space direction="vertical" size={0}>
              <Badge status="error" text="已鎖定" />
              <Text type="danger" style={{ fontSize: '12px' }}>
                失敗 {user.failedLoginCount || 0} 次
              </Text>
              {user.lockReason && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {user.lockReason}
                </Text>
              )}
            </Space>
          );
        }
        
        const display = userUtils.getStatusDisplay(isActive);
        return (
          <Badge 
            status={isActive ? "success" : "default"} 
            text={display.text}
          />
        );
      },
    },
    {
      title: '最後登入',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 140,
      sorter: true,
      render: (date: string) => {
        if (!date) return <Text type="secondary">從未登入</Text>;
        try {
          return (
            <Text type="secondary">
              {new Date(date).toLocaleString('zh-TW')}
            </Text>
          );
        } catch (e) {
          return <Text type="secondary">-</Text>;
        }
      },
    },
    {
      title: '建立日期',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      sorter: true,
      render: (date: string) => {
        if (!date) return <Text type="secondary">-</Text>;
        try {
          return (
            <Text type="secondary">
              {new Date(date).toLocaleDateString('zh-TW')}
            </Text>
          );
        } catch (e) {
          return <Text type="secondary">-</Text>;
        }
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, user: User) => (
        <Space size="small">
          <Tooltip title="檢視">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleView(user.userId)}
            />
          </Tooltip>
          <Tooltip title="編輯">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(user.userId)}
            />
          </Tooltip>
          <Dropdown menu={getActionMenu(user)} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys: state.selectedRowKeys,
    onChange: handleSelectChange,
  };

  return (
    <div className="user-list-page">
      {/* Header Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" className="user-stats-card" style={{ textAlign: 'center' }}>
            <Statistic
              title="總使用者數"
              value={state.statistics.total}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" className="user-stats-card" style={{ textAlign: 'center' }}>
            <Statistic
              title="啟用使用者"
              value={state.statistics.active}
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" className="user-stats-card" style={{ textAlign: 'center' }}>
            <Statistic
              title="停用使用者"
              value={state.statistics.inactive}
              prefix={<UserOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Card 
        style={{ 
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          marginTop: '24px'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ margin: 0, color: '#262626' }}>
                使用者管理
              </Title>
              <Text type="secondary">管理系統使用者和權限</Text>
            </Col>
            <Col>
              <Space>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'group',
                        label: '群組權限管理',
                        icon: <TeamOutlined />,
                        onClick: () => navigate('/user/permissions'),
                      },
                      {
                        key: 'personal',
                        label: '個人權限管理',
                        icon: <UserOutlined />,
                        onClick: () => navigate('/user/personal-permissions'),
                      },
                    ],
                  }}
                  trigger={['click']}
                >
                  <Button 
                    icon={<SettingOutlined />} 
                    size="large"
                  >
                    權限管理
                  </Button>
                </Dropdown>
                <Button 
                  icon={<MonitorOutlined />} 
                  size="large"
                  onClick={() => navigate('/user/monitoring')}
                >
                  使用者監控
                </Button>
                <Button icon={<ImportOutlined />} size="large">
                  匯入
                </Button>
                <Button icon={<ExportOutlined />} size="large">
                  匯出
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={handleCreate}
                  style={{ borderRadius: '6px' }}
                >
                  新增使用者
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '24px 0' }} />

        {/* Search and Filters */}
        <div className="filter-card" style={{ 
          background: '#fafafa', 
          padding: '16px', 
          borderRadius: '6px',
          marginBottom: '24px' 
        }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <Search
                placeholder="搜尋使用者ID或姓名"
                allowClear
                size="large"
                onSearch={handleSearch}
                prefix={<SearchOutlined />}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} md={4}>
              <Select
                placeholder="狀態"
                allowClear
                size="large"
                style={{ width: '100%' }}
                onChange={(value) => handleFilterChange('isActive', value)}
              >
                <Option value={true}>啟用</Option>
                <Option value={false}>停用</Option>
              </Select>
            </Col>
            <Col xs={24} md={4}>
              <Button 
                icon={<ReloadOutlined />}
                size="large"
                onClick={() => {
                  loadUsers();
                  loadStatistics();
                }}
                block
              >
                重新整理
              </Button>
            </Col>
          </Row>
        </div>

        {/* Batch Actions */}
        {state.selectedRowKeys.length > 0 && (
          <div style={{ 
            marginBottom: '16px',
            padding: '12px 16px',
            background: '#e6f7ff',
            borderRadius: '6px',
            border: '1px solid #91d5ff'
          }}>
            <Space>
              <Text strong>已選擇 {state.selectedRowKeys.length} 項</Text>
              <Button 
                danger 
                onClick={() => handleBatchAction('delete')}
                size="small"
              >
                批量刪除
              </Button>
              <Button 
                onClick={() => handleBatchAction('export')}
                size="small"
              >
                匯出選中
              </Button>
            </Space>
          </div>
        )}

        {/* Table */}
        <Spin spinning={state.loading}>
          <Table
            columns={columns}
            dataSource={state.users}
            rowKey="userId"
            loading={false}
            pagination={{
              current: state.pagination.current,
              pageSize: state.pagination.pageSize,
              total: state.pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
              pageSizeOptions: ['10', '20', '50', '100'],
              style: { marginTop: '16px' }
            }}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: (
                <Empty 
                  description="暫無使用者資料" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
            }}
            rowClassName={(record, index) => 
              `user-table-row ${index % 2 === 0 ? 'user-table-row-even' : 'user-table-row-odd'} ${!record.isActive ? 'user-table-row-inactive' : ''}`
            }
          />
        </Spin>
      </Card>
    </div>
  );
};

export default UserList;